import { useId } from 'react';

interface BoringAvatarProps {
  name: string;
  size?: number | string;
  square?: boolean;
  colors?: string[];
  className?: string;
  style?: React.CSSProperties;
}

const SHAPE_COUNT = 4;
const VIEWBOX = 80;

const DEFAULT_COLORS = ['#084386', '#64a6f7', '#C87941', '#D4A76A', '#8B5E3C'];

function hash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function digitAt(n: number, pos: number): number {
  return Math.floor(n / 10 ** pos % 10);
}

function isEvenDigit(n: number, pos: number): boolean {
  return !(digitAt(n, pos) % 2);
}

function offset(n: number, max: number, axis?: number): number {
  const v = n % max;
  return axis != null && digitAt(n, axis) % 2 === 0 ? -v : v;
}

function pick(n: number, arr: string[], len: number): string {
  return arr[n % len];
}

interface Shape {
  color: string;
  translateX: number;
  translateY: number;
  rotate: number;
  isSquare: boolean;
}

function generateShapes(name: string, colors: string[]): Shape[] {
  const h = hash(name);
  const len = colors.length;
  return Array.from({ length: SHAPE_COUNT }, (_, i) => ({
    color: pick(h + i, colors, len),
    translateX: offset(h * (i + 1), VIEWBOX / 2 - (i + 17), 1),
    translateY: offset(h * (i + 1), VIEWBOX / 2 - (i + 17), 2),
    rotate: offset(h * (i + 1), 360),
    isSquare: isEvenDigit(h, 2),
  }));
}

export default function BoringAvatar({
  name,
  size = 48,
  square = false,
  colors = DEFAULT_COLORS,
  className,
  style,
}: BoringAvatarProps) {
  const id = useId();
  const shapes = generateShapes(name, colors);

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      fill="none"
      role="img"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      style={style}
    >
      <title>{name}</title>
      <mask id={id} maskUnits="userSpaceOnUse" x={0} y={0} width={VIEWBOX} height={VIEWBOX}>
        <rect width={VIEWBOX} height={VIEWBOX} rx={square ? undefined : VIEWBOX * 2} fill="#FFFFFF" />
      </mask>
      <g mask={`url(#${id})`}>
        <rect width={VIEWBOX} height={VIEWBOX} fill={shapes[0].color} />
        <rect
          x={(VIEWBOX - 60) / 2}
          y={(VIEWBOX - 20) / 2}
          width={VIEWBOX}
          height={shapes[1].isSquare ? VIEWBOX : VIEWBOX / 8}
          fill={shapes[1].color}
          transform={`translate(${shapes[1].translateX} ${shapes[1].translateY}) rotate(${shapes[1].rotate} ${VIEWBOX / 2} ${VIEWBOX / 2})`}
        />
        <circle
          cx={VIEWBOX / 2}
          cy={VIEWBOX / 2}
          fill={shapes[2].color}
          r={VIEWBOX / 5}
          transform={`translate(${shapes[2].translateX} ${shapes[2].translateY})`}
        />
        <line
          x1={0}
          y1={VIEWBOX / 2}
          x2={VIEWBOX}
          y2={VIEWBOX / 2}
          strokeWidth={2}
          stroke={shapes[3].color}
          transform={`translate(${shapes[3].translateX} ${shapes[3].translateY}) rotate(${shapes[3].rotate} ${VIEWBOX / 2} ${VIEWBOX / 2})`}
        />
      </g>
    </svg>
  );
}
